import React, { useState, useEffect } from 'react';
import { runWalkForward } from '../../api';
import { StatCard } from './utils';
import OptimizationPanel from './OptimizationPanel';
import { useBackgroundRuns } from '../../context/BackgroundRunsContext';

function addDaysIso(isoDate, days) {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoDate;
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function daysBetweenIso(start, end) {
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 1;
  return Math.max(1, Math.round((e - s) / 86400000));
}

function defaultOosFoldsForBacktest(backtest) {
  const start = backtest?.start_date;
  const end = backtest?.end_date;
  if (!start || !end) return [];

  const span = daysBetweenIso(start, end);
  const preEnd = addDaysIso(start, -1);
  const preStart = addDaysIso(preEnd, -(span - 1));
  const postStart = addDaysIso(end, 1);
  const postEnd = addDaysIso(postStart, span - 1);

  return [
    { label: 'pre_period_oos', test_start: preStart, test_end: preEnd },
    { label: 'post_period_oos', test_start: postStart, test_end: postEnd },
  ];
}

export default function WalkForwardPanel({ backtest, strategyId, strategyForEdit, onStrategyUpdated, onRerunResult }) {
  const [walkForwardBusy, setWalkForwardBusy] = useState(false);
  const [walkForwardError, setWalkForwardError] = useState('');
  const [walkForwardInfo, setWalkForwardInfo] = useState('');
  const [walkForwardData, setWalkForwardData] = useState(backtest?.results?.walk_forward || null);
  const [walkForwardFolds, setWalkForwardFolds] = useState([]);
  const { startRun } = useBackgroundRuns();
  const [walkForwardEditOpen, setWalkForwardEditOpen] = useState(false);

  useEffect(() => {
    setWalkForwardData(backtest?.results?.walk_forward || null);
    setWalkForwardError('');
    setWalkForwardInfo('');
  }, [backtest?.id, backtest?.results?.walk_forward]);

  useEffect(() => {
    const fromResult = (backtest?.results?.walk_forward?.folds || [])
      .map((f, idx) => ({
        label: f.label || `fold_${idx + 1}`,
        test_start: f.test_start || '',
        test_end: f.test_end || '',
      }))
      .filter((f) => f.test_start && f.test_end);

    if (fromResult.length > 0) {
      setWalkForwardFolds(fromResult);
      return;
    }

    setWalkForwardFolds(
      defaultOosFoldsForBacktest({
        start_date: backtest?.start_date,
        end_date: backtest?.end_date,
      })
    );
  }, [
    backtest?.id,
    backtest?.start_date,
    backtest?.end_date,
    backtest?.results?.walk_forward?.generated_at,
    backtest?.results?.walk_forward?.folds,
  ]);

  const handleRunWalkForward = async () => {
    if (!backtest?.id) return;
    const payloadFolds = (walkForwardFolds || [])
      .filter((f) => f?.test_start && f?.test_end)
      .map((f, idx) => ({
        label: String(f.label || `fold_${idx + 1}`),
        test_start: f.test_start,
        test_end: f.test_end,
      }));

    if (payloadFolds.length === 0) {
      setWalkForwardError('Add at least one fold with a valid test_start and test_end.');
      return;
    }

    setWalkForwardBusy(true);
    setWalkForwardError('');
    setWalkForwardInfo('');
    try {
      const resp = await runWalkForward(backtest.id, {
        scheme: 'external_oos',
        folds: payloadFolds,
      });
      if (resp?.data?.queued) {
        setWalkForwardInfo(resp.data.detail || 'Walk-forward analysis queued in the background.');
        startRun({
          backtestId: backtest.id,
          kind: 'walk_forward',
          onComplete: (data) => {
            setWalkForwardData(data?.results?.walk_forward || null);
            setWalkForwardInfo('Walk-forward analysis completed.');
          },
        });
        return;
      }
      setWalkForwardData(resp.data?.walk_forward || null);
      setWalkForwardInfo('Walk-forward analysis completed.');
    } catch (err) {
      setWalkForwardError(err.response?.data?.detail || 'Failed to run walk-forward analysis.');
    } finally {
      setWalkForwardBusy(false);
    }
  };

  return (
    <div style={{
      background: 'var(--card)',
      border: '1px solid var(--border)',
      borderRadius: 10,
      padding: 14,
      marginBottom: 24,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Walk-Forward Analysis</div>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            External OOS validation. By default, folds cover equal periods before and after your backtest window.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={() => setWalkForwardEditOpen((v) => !v)}
            style={{
              background: 'var(--surface)',
              color: 'var(--text)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '8px 12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {walkForwardEditOpen ? 'Hide Folds' : 'Edit Folds'}
          </button>
          <button
            onClick={handleRunWalkForward}
            disabled={walkForwardBusy}
            style={{
              background: walkForwardBusy ? 'var(--subtle)' : 'var(--teal)',
              color: 'var(--bg)',
              border: 'none',
              borderRadius: 8,
              padding: '8px 12px',
              fontWeight: 700,
              cursor: walkForwardBusy ? 'wait' : 'pointer',
            }}
          >
            {walkForwardBusy ? 'Running…' : (walkForwardData ? 'Re-run Walk-Forward' : 'Run Walk-Forward')}
          </button>
        </div>
      </div>

      {walkForwardEditOpen && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            Define custom OOS folds. Each fold runs on its test range plus hidden warmup bars.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Label</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Test Start</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Test End</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {walkForwardFolds.map((fold, idx) => (
                  <tr key={`${idx}-${fold.label}`} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <input
                        type="text"
                        value={fold.label || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setWalkForwardFolds((prev) => prev.map((f, i) => (i === idx ? { ...f, label: v } : f)));
                        }}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '5px 7px' }}
                      />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input
                        type="date"
                        value={fold.test_start || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setWalkForwardFolds((prev) => prev.map((f, i) => (i === idx ? { ...f, test_start: v } : f)));
                        }}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '5px 7px' }}
                      />
                    </td>
                    <td style={{ padding: '6px 8px' }}>
                      <input
                        type="date"
                        value={fold.test_end || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          setWalkForwardFolds((prev) => prev.map((f, i) => (i === idx ? { ...f, test_end: v } : f)));
                        }}
                        style={{ width: '100%', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', padding: '5px 7px' }}
                      />
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                      <button
                        onClick={() => setWalkForwardFolds((prev) => prev.filter((_, i) => i !== idx))}
                        style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8 }}>
            <button
              onClick={() => {
                const idx = (walkForwardFolds?.length || 0) + 1;
                setWalkForwardFolds((prev) => [
                  ...prev,
                  {
                    label: `custom_fold_${idx}`,
                    test_start: backtest?.start_date || '',
                    test_end: backtest?.end_date || '',
                  },
                ]);
              }}
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}
            >
              + Add Fold
            </button>
          </div>
        </div>
      )}

      {walkForwardError && (
        <div style={{ marginTop: 8, color: 'var(--red)', fontSize: 12 }}>{walkForwardError}</div>
      )}

      {walkForwardInfo && (
        <div style={{ marginTop: 8, color: 'var(--teal)', fontSize: 12 }}>{walkForwardInfo}</div>
      )}

      {walkForwardData?.summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
          gap: 10,
          marginTop: 12,
        }}>
          <StatCard label="Folds" value={walkForwardData.summary.fold_count ?? '—'} />
          <StatCard label="Mean OOS Return" value={walkForwardData.summary.mean_return_pct != null ? `${walkForwardData.summary.mean_return_pct}%` : '—'} />
          <StatCard label="Median OOS Return" value={walkForwardData.summary.median_return_pct != null ? `${walkForwardData.summary.median_return_pct}%` : '—'} />
          <StatCard label="Fold Win Rate" value={walkForwardData.summary.win_rate_pct != null ? `${walkForwardData.summary.win_rate_pct}%` : '—'} />
          <StatCard label="Mean Sharpe" value={walkForwardData.summary.mean_sharpe_ratio ?? '—'} />
          <StatCard label="Worst Drawdown" value={walkForwardData.summary.worst_max_drawdown_pct != null ? `${walkForwardData.summary.worst_max_drawdown_pct}%` : '—'} />
        </div>
      )}

      {Array.isArray(walkForwardData?.folds) && walkForwardData.folds.length > 0 && (
        <div style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Fold</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Train</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--muted)' }}>Test</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Return</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Sharpe</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Max DD</th>
                <th style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--muted)' }}>Trades</th>
              </tr>
            </thead>
            <tbody>
              {walkForwardData.folds.map((f) => (
                <tr key={f.fold} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '6px 8px', color: 'var(--text)' }}>{f.label || f.fold}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text)' }}>{f.train_start} to {f.train_end}</td>
                  <td style={{ padding: '6px 8px', color: 'var(--text)' }}>{f.test_start} to {f.test_end}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                    {f.ok === false ? '—' : (f.metrics?.return_pct != null ? `${f.metrics.return_pct}%` : '—')}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                    {f.ok === false ? '—' : (f.metrics?.sharpe_ratio ?? '—')}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                    {f.ok === false ? '—' : (f.metrics?.max_drawdown_pct != null ? `${f.metrics.max_drawdown_pct}%` : '—')}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--text)' }}>
                    {f.ok === false ? '—' : (f.metrics?.total_trades ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Array.isArray(walkForwardData?.folds) && walkForwardData.folds.some((f) => f.ok === false) && (
        <div style={{ marginTop: 8, color: 'var(--muted)', fontSize: 12 }}>
          Some folds failed due to missing market data or insufficient bars for that date range.
        </div>
      )}

      <OptimizationPanel
        backtest={backtest}
        strategyId={strategyId}
        strategyForEdit={strategyForEdit}
        onStrategyUpdated={onStrategyUpdated}
        onRerunResult={onRerunResult}
        folds={walkForwardFolds}
      />
    </div>
  );
}
